import api from '@/shared/utils/apiConfig';
import i18n from '@/shared/i18n';
import { formatPersonName } from '@/shared/utils/personName';

export type TaskStatus = 'todo' | 'in_progress' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high';

export interface TaskStaffSummary {
  id: string;
  firstName: string;
  lastName: string;
  staffCode?: string;
  designation?: string;
  avatarUrl?: string | null;
}

export interface TaskClinicSummary {
  id: string;
  name: string;
  branchCode?: string;
  shortName?: string;
}

export interface TaskUserSummary {
  id: string;
  name: string;
  designation?: string;
}

export interface TaskChecklistItem {
  id: string;
  text: string;
  done: boolean;
  sortOrder: number;
  assigneeId?: string;
  assignee?: TaskStaffSummary;
  dueDate?: string;
}

export interface TaskRelatedEntity {
  type: 'lead' | 'patient' | 'lab_order';
  id: string;
  label?: string;
}

export interface TaskChatSource {
  threadId: string;
  messageId: string;
}

export interface TaskProjectSummary {
  id: string;
  name: string;
  isInbox: boolean;
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  projectId: string;
  project?: TaskProjectSummary;
  assigneeId: string;
  assignee?: TaskStaffSummary;
  clinicId?: string;
  clinic?: TaskClinicSummary;
  dueDate?: string;
  startDate?: string;
  checklist: TaskChecklistItem[];
  checklistDone: number;
  checklistTotal: number;
  relatedEntity?: TaskRelatedEntity;
  source?: TaskChatSource;
  createdBy: TaskUserSummary;
  completedBy?: TaskUserSummary;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistItemInput {
  id?: string;
  text: string;
  done?: boolean;
  sortOrder?: number;
  assigneeId?: string | null;
  dueDate?: string | null;
}

export interface CreateTaskPayload {
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeId: string;
  projectId?: string;
  clinicId?: string | null;
  dueDate?: string | null;
  startDate?: string | null;
  checklist?: ChecklistItemInput[];
  relatedEntity?: { type: 'lead' | 'patient' | 'lab_order'; id: string } | null;
  source?: TaskChatSource;
}

export interface UpdateTaskPayload {
  title?: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeId?: string;
  projectId?: string | null;
  clinicId?: string | null;
  dueDate?: string | null;
  startDate?: string | null;
  checklist?: ChecklistItemInput[];
  relatedEntity?: { type: 'lead' | 'patient' | 'lab_order'; id: string } | null;
}

export interface TaskListParams {
  status?: TaskStatus | string;
  priority?: TaskPriority | string;
  assigneeId?: string;
  projectId?: string;
  clinicId?: string;
  mine?: boolean | string;
  createdByMe?: boolean | string;
  search?: string;
  dueFrom?: string;
  dueTo?: string;
  /** `0`/`false` = no due date; `1`/`true` = has due date. */
  hasDueDate?: boolean | string;
  relatedEntityType?: 'lead' | 'patient' | 'lab_order' | string;
  relatedEntityId?: string;
  sourceThreadId?: string;
  page?: number;
  limit?: number;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  count?: number;
  total?: number;
  page?: number;
  pages?: number;
  message?: string;
}

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  get todo() { return i18n.t('task:status.todo'); },
  get in_progress() { return i18n.t('task:status.in_progress'); },
  get done() { return i18n.t('task:status.done'); },
  get cancelled() { return i18n.t('task:status.cancelled'); },
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  get low() { return i18n.t('task:priority.low'); },
  get medium() { return i18n.t('task:priority.medium'); },
  get high() { return i18n.t('task:priority.high'); },
};

export const formatStaffName = (staff?: TaskStaffSummary | null): string => {
  if (!staff) return '—';
  const name = formatPersonName(staff);
  return staff.staffCode ? `${name} (${staff.staffCode})` : name || '—';
};

export const taskAPI = {
  getAll: async (params: TaskListParams = {}): Promise<ApiResponse<Task[]>> => {
    const response = await api.get('/tasks', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Task>> => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },

  create: async (payload: CreateTaskPayload): Promise<ApiResponse<Task>> => {
    const response = await api.post('/tasks', payload);
    return response.data;
  },

  update: async (id: string, payload: UpdateTaskPayload): Promise<ApiResponse<Task>> => {
    const response = await api.put(`/tasks/${id}`, payload);
    return response.data;
  },

  toggleChecklistItem: async (
    taskId: string,
    itemId: string
  ): Promise<ApiResponse<Task>> => {
    const response = await api.patch(`/tasks/${taskId}/checklist/${itemId}`);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<Record<string, never>>> => {
    const response = await api.delete(`/tasks/${id}`);
    return response.data;
  },
};
